import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, Show } from "solid-js";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import type {
  CandidateJourney,
  JourneyPage,
  PriorityContentFlow,
} from "./journeyTypes";
import { pageDetailHref } from "./pageDetailNavigation";
import { compactRouteLabel, uniqueRouteLabels } from "./priorityFlowLabels";
import { clusterContextForPages } from "./scanModel";

export function JourneySelectedPanel(props: {
  data: OverviewResponse;
  websiteId: string;
  flow: PriorityContentFlow | null;
  journey: CandidateJourney | null;
  selectedPage: JourneyPage | null;
  returnQuery: string;
}) {
  const clusterContext = createMemo(() =>
    clusterContextForPages(props.data.pages),
  );
  const selectedStep = () =>
    props.journey?.steps.find(
      (step) => step.page.id === props.selectedPage?.id,
    ) ?? null;
  const inspectionPage = () =>
    props.selectedPage ?? props.journey?.steps[0]?.page;
  const selectedRouteLabel = () => {
    if (!props.flow || !props.journey) return null;
    return (
      uniqueRouteLabels(
        [props.journey, ...props.flow.paths],
        props.data.site,
        clusterContext(),
      ).get(props.journey.id) ??
      compactRouteLabel(props.journey, props.data.site, clusterContext())
    );
  };
  const structureHref = () => {
    const params = new URLSearchParams(props.returnQuery);
    params.set("view", "structure");
    if (inspectionPage()) {
      params.set("page", inspectionPage()?.id ?? "");
    }
    return `/w/${props.websiteId}/scan/pages?${params.toString()}`;
  };

  return (
    <aside
      class="journey-selected-panel"
      aria-label="Selected current path context"
    >
      <Show
        when={props.flow}
        keyed
        fallback={
          <div class="journey-panel-empty">
            <span class="signal-mini-kicker">Current Paths</span>
            <h2>Select a current path</h2>
            <p>Review its representative route and supported concerns.</p>
          </div>
        }
      >
        {(flow) => (
          <>
            <div class="journey-panel-head">
              <div>
                <span class="signal-mini-kicker">Selected current path</span>
                <h2>{flow.label}</h2>
              </div>
              <span class={`is-${flow.priorityTier}`}>
                {flow.neutralRepresentative
                  ? "Representative"
                  : flow.priorityTier}
              </span>
            </div>
            <Show when={props.journey} keyed>
              {(journey) => (
                <p class="journey-selected-route-label">
                  {selectedRouteLabel() ??
                    compactRouteLabel(
                      journey,
                      props.data.site,
                      clusterContext(),
                    )}
                </p>
              )}
            </Show>
            <dl class="journey-panel-facts">
              <div>
                <dt>Unique pages</dt>
                <dd>{flow.uniquePages.length}</dd>
              </div>
              <div>
                <dt>Related routes</dt>
                <dd>{flow.paths.length}</dd>
              </div>
              <div>
                <dt>Priority concerns</dt>
                <dd>{flow.priorityConcernCount}</dd>
              </div>
            </dl>
            <div class="journey-panel-section">
              <h3>Why this path was prioritised</h3>
              <p>{flow.priorityReason}</p>
            </div>
            <Show when={props.journey} keyed>
              {(journey) => (
                <div class="journey-panel-section">
                  <h3>Representative route coverage</h3>
                  <p>
                    {journey.assessedPages} of {journey.steps.length} route
                    pages have assessment evidence.
                  </p>
                </div>
              )}
            </Show>
            <Show when={inspectionPage()} keyed>
              {(page) => (
                <div class="journey-selected-page">
                  <span>
                    Current selected page
                    {selectedStep() ? ` / ${selectedStep()?.roleLabel}` : ""}
                  </span>
                  <strong>{pageDisplayLabel(page, props.data.site)}</strong>
                  <small>{rawPagePath(page)}</small>
                  <Show when={selectedStep()}>
                    <p>
                      {selectedStep()?.inboundCount} recorded inbound /{" "}
                      {selectedStep()?.outboundCount} recorded outbound links
                    </p>
                  </Show>
                  <a
                    href={pageDetailHref(props.websiteId, page.id, {
                      source: "pages",
                      returnQuery: props.returnQuery,
                    })}
                  >
                    Open Page Detail
                  </a>
                </div>
              )}
            </Show>
            <div class="journey-panel-actions">
              <Show when={inspectionPage()}>
                <a class="signal-dark-button" href={structureHref()}>
                  Locate in current structure
                </a>
              </Show>
            </div>
          </>
        )}
      </Show>
    </aside>
  );
}
